/**
 * User Service
 * Handles all user-related API calls
 */

import { fetchAPI, AUTH_TOKEN, REFRESH_TOKEN } from './apiClient';

interface SigninResponse {
  token: string;
  refresh_token: string;
  user?: any;
}

interface SignupResponse {
  token: string;
  refresh_token: string;
  user?: any;
}

interface User {
  id: number;
  username: string;
  email: string;
  name?: string;
}

export const userService = {
  /**
   * Sign in user
   */
  async signin(username: string, password: string, name: string = ''): Promise<SigninResponse> {
    return fetchAPI('/api/user/signin/', {
      method: 'POST',
      body: JSON.stringify({ username, password, name }),
      requiresAuth: false,
    });
  },

  /**
   * Sign up new user
   */
  async signup(username: string,email:string, password: string): Promise<SignupResponse> {
    return fetchAPI('/api/user/signup/', {
      method: 'POST',
      body: JSON.stringify({ username,email,password }),
      requiresAuth: false,
    });
  },

  /**
   * Get current user details
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetchAPI<{ results?: User[] }>('/api/user/');
      // API returns paginated results, get first user (current user)
      return response.results && response.results.length > 0 ? response.results[0] : null;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return fetchAPI('/api/password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      requiresAuth: false,
    });
  },

  /**
   * Validate reset token
   */
  async validateResetToken(token: string): Promise<{ status: string }> {
    return fetchAPI('/api/password-reset/validate_token/', {
      method: 'POST',
      body: JSON.stringify({ token }),
      requiresAuth: false,
    });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return fetchAPI('/api/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      requiresAuth: false,
    });
  },

  /**
   * Exchange the refresh token for a fresh access token.
   * Called internally by apiClient on a 401; not normally called directly.
   */
  async refresh(refreshToken: string): Promise<{ token: string }> {
    return fetchAPI('/api/user/refresh/', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      requiresAuth: false,
    });
  },

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
  },
};
